/**
 * AI CONTEXT: support-system - Floating support chat bubble component
 * Journal: J-20250817-01
 * 
 * Purpose:
 * - Provide easy access to customer support and inquiries
 * - Float above mobile navigation on mobile devices
 * - Auto-collect user information from Cognito/database
 * - Rate limit submissions to prevent spam
 * 
 * Architecture decisions:
 * - Position above mobile navigation (z-index: 50)
 * - Use glassmorphism design consistent with app theme
 * - Implement rate limiting (1 submission per minute)
 * - Auto-populate user data from authentication context
 * 
 * Future considerations:
 * - Add chat history for returning users
 * - Implement real-time chat functionality
 * - Add file attachment support
 * - Consider AI-powered support suggestions
 */

import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

import SupportModal from './SupportModal';

const SupportChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const bubbleRef = useRef(null);

  // Check rate limiting on mount and when user changes
  useEffect(() => {
    const stored = localStorage.getItem('support_last_submission');
    if (stored) {
      const lastTime = parseInt(stored, 10);
      setLastSubmissionTime(lastTime);
      
      // Check if still rate limited
      const timeSinceLastSubmission = Date.now() - lastTime;
      const rateLimitWindow = 60000; // 1 minute
      
      if (timeSinceLastSubmission < rateLimitWindow) {
        setIsRateLimited(true);
        // Auto-disable rate limiting when time expires
        const timeRemaining = rateLimitWindow - timeSinceLastSubmission;
        setTimeout(() => {
          setIsRateLimited(false);
        }, timeRemaining);
      }
    }
  }, [user]);

  const handleOpen = () => {
    if (isRateLimited) {
      const timeRemaining = Math.ceil((60000 - (Date.now() - lastSubmissionTime)) / 1000);
      alert(`Please wait ${timeRemaining} seconds before submitting another inquiry.`);
      return;
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmission = () => {
    const now = Date.now();
    setLastSubmissionTime(now);
    localStorage.setItem('support_last_submission', now.toString());
    setIsRateLimited(true);
    
    // Auto-disable rate limiting after 1 minute
    setTimeout(() => {
      setIsRateLimited(false);
    }, 60000);
    
    setIsOpen(false);
  };

  // Don't show if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Floating Chat Bubble */}
      <div 
        ref={bubbleRef}
        className="fixed bottom-24 right-4 z-50 md:bottom-8 md:right-8"
        style={{ zIndex: 50 }} // Ensure it's above mobile navigation
      >
        <button
          onClick={handleOpen}
          disabled={isRateLimited}
          className={`
            relative w-16 h-16 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110
            ${isRateLimited 
              ? 'bg-gray-500 cursor-not-allowed' 
              : 'bg-custom-orange hover:bg-custom-orange/90 cursor-pointer'
            }
            flex items-center justify-center text-white
          `}
          title={isRateLimited ? 'Rate limited - please wait' : 'Get Support'}
        >
          {isRateLimited ? (
            <X className="h-6 w-6" />
          ) : (
            <MessageCircle className="h-6 w-6" />
          )}
          
          {/* Rate limit indicator */}
          {isRateLimited && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-xs text-white">⏰</span>
            </div>
          )}
        </button>
        
        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-theme-black text-theme-silver text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
          {isRateLimited ? 'Rate limited' : 'Get Support'}
          <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-theme-black"></div>
        </div>
      </div>

      {/* Support Modal */}
      {isOpen && (
        <SupportModal
          user={user}
          onClose={handleClose}
          onSubmit={handleSubmission}
          isRateLimited={isRateLimited}
        />
      )}
    </>
  );
};

export default SupportChatBubble;
